import React, { useState } from 'react';
import { Building2, Users, Lock, Calculator, Layers, Plug, Bell, Globe, FileText, Palette, ScrollText, CreditCard } from 'lucide-react';
import { sans, coral, coralSoft, useTheme } from '../../tokens';
import CompanySettings from './CompanySettings';
import UsersRolesSettings from './UsersRolesSettings';
import NumberFormatSettings from './NumberFormatSettings';
import SecuritySettings from './SecuritySettings';
import FinancialSettings from './FinancialSettings';
import AuditTrailSettings from './AuditTrailSettings';
import EquitySettings from './EquitySettings';
import IntegrationsSettings from './IntegrationsSettings';
import NotificationsSettings from './NotificationsSettings';
import PortalSettings from './PortalSettings';
import ReportsSettings from './ReportsSettings';
import AppearanceSettings from './AppearanceSettings';
import BillingSettings from './BillingSettings';

const SETTINGS_SECTIONS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'financial', label: 'Financial', icon: Calculator },
  { id: 'equity', label: 'Equity', icon: Layers },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'portal', label: 'Investor Portal', icon: Globe },
  { id: 'reports', label: 'Reports & Exports', icon: FileText },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'audit', label: 'Audit Trail', icon: ScrollText },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

export default function SettingsPage({ isDark, setIsDark, lang, setLang, currency, setCurrency, hasMfaEnrolled, showMfaSetup, setShowMfaSetup }) {
  const t = useTheme();
  const [activeSection, setActiveSection] = useState('company');

  function renderSection(sectionId) {
    switch (sectionId) {
      case 'company':
        return <CompanySettings currency={currency} setCurrency={setCurrency} />;
      case 'users':
        return <UsersRolesSettings />;
      case 'financial':
        return <NumberFormatSettings />;
      case 'security':
        return <SecuritySettings hasMfaEnrolled={hasMfaEnrolled} showMfaSetup={showMfaSetup} setShowMfaSetup={setShowMfaSetup} />;
      case 'equity':
        return <EquitySettings />;
      case 'integrations':
        return <IntegrationsSettings />;
      case 'notifications':
        return <NotificationsSettings />;
      case 'portal':
        return <PortalSettings />;
      case 'reports':
        return <ReportsSettings />;
      case 'appearance':
        return <AppearanceSettings isDark={isDark} setIsDark={setIsDark} lang={lang} setLang={setLang} />;
      case 'audit':
        return <AuditTrailSettings />;
      case 'billing':
        return <BillingSettings />;
      default:
        return <FinancialSettings />;
    }
  }

  return (
    <div style={{ display: 'flex' }}>
      {/* Sidebar — fixed left, same as Investor SideNav */}
      <div style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: 180,
        height: '100vh',
        overflowY: 'auto',
        background: t.bgCard,
        borderRight: '1px solid ' + t.border,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '120px 8px 0 8px',
      }}>
        <div style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: t.textMuted,
          fontFamily: sans,
          padding: '0 12px 12px',
        }}>
          Settings
        </div>

        {SETTINGS_SECTIONS.map(s => {
          const Icon = s.icon;
          const isActive = activeSection === s.id;
          return (
            <div
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: isActive ? 600 : 400,
                fontFamily: sans,
                background: isActive ? coralSoft : 'transparent',
                color: isActive ? coral : t.textMuted,
                borderLeft: isActive ? '3px solid ' + coral : '3px solid transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <Icon size={14} />
              {s.label}
            </div>
          );
        })}
      </div>

      {/* Content */}
      <div style={{
        marginLeft: 180,
        flex: 1,
        padding: '24px 32px',
        maxWidth: 1200,
      }}>
        {renderSection(activeSection)}
      </div>
    </div>
  );
}
